import {
  QueryClient,
  QueryClientProvider,
  MutationCache,
  QueryCache,
} from "@tanstack/react-query";
import { PanelProvider } from "./context/panel-context";
import { PanelPropsProvider } from "./context/panel-props-context";
import { ProjectProvider } from "./context/current-project-context";
import { AuthProvider } from "react-oidc-context";
import { oidcConfig } from "./config";
import { ThemeProvider } from "./context/theme-context";
import Routes from "./routes";
import { ChatInputProvider } from "./context/chat-input-context";
import { Neo4jVisualizerProvider } from "./context/neo4j-visualizer-context";
import { AppErrorBoundary } from "./components/elements/error-boundary";
import Neo4jVisualizer from "./components/elements/neo4j-visualizer";
import { useNeo4jVisualizer } from "./context/neo4j-visualizer-context";
import { useEffect } from "react";
import { UnsavedChangesProvider, useUnsavedChanges } from "./context/unsaved-changes-context";
import ConfirmDialog from "./components/elements/confirm-dialog";

const handleGlobalApiError = (error: unknown) => {
  const err = error as { status?: number; message?: string };
  if (err?.status === 401) return;
  if (err?.status === 403) {
    err.message = "You don't have permission to perform this action.";
    queryClient.invalidateQueries({ queryKey: ["project-members"] });
  }
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      // Several pages poll in-progress generation status on short intervals
      // (down to 2s). Without this, every one of those timers keeps firing
      // at full speed while the browser tab is backgrounded/minimized, since
      // TanStack Query's own default for `refetchIntervalInBackground` is
      // true. The user isn't watching a backgrounded tab's progress ring in
      // real time, so there's nothing lost by only catching up once it's
      // focused again (`refetchOnWindowFocus` can still be enabled per-query
      // if a page wants that).
      refetchIntervalInBackground: false,
    },
  },
  mutationCache: new MutationCache({ onError: handleGlobalApiError }),
  queryCache: new QueryCache({ onError: handleGlobalApiError }),
});

// Component that uses the visualizer context - must be inside the provider
function AppContent() {
  const { isOpen, focusNodeId, closeVisualizer } = useNeo4jVisualizer();
  const {
    showDialog,
    setShowDialog,
    pendingAction,
    setPendingAction,
    setHasUnsavedChanges,
  } = useUnsavedChanges();

  return (
    <>
      <Routes />
      {isOpen && (
        <Neo4jVisualizer
          isOpen={isOpen}
          onClose={closeVisualizer}
          focusNodeId={focusNodeId || undefined}
        />
      )}

      {/* Global Unsaved Changes Confirmation Dialog */}
      <ConfirmDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        onConfirm={() => {
          // Store the action before clearing state
          if (pendingAction) {
            const actionToExecute = pendingAction;
            // Clear state first
            setPendingAction(null);
            setShowDialog(false);
            setHasUnsavedChanges(false);
            // Execute the action after state is cleared
            actionToExecute();
          } else {
            setShowDialog(false);
            setHasUnsavedChanges(false);
          }
        }}
        title="Unsaved Changes"
        description="Please save the screen."
        confirmText="Continue Without Saving"
        cancelText="Cancel"
      />
    </>
  );
}

function App() {
  // Global error handler for unhandled promise rejections and errors
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error("Unhandled promise rejection:", event.reason);

      // Check if it's a chunk load error
      if (
        event.reason?.message?.includes("Failed to fetch dynamically imported module") ||
        event.reason?.message?.includes("Importing a module script failed") ||
        event.reason?.message?.toLowerCase().includes("chunk")
      ) {
        console.warn(
          "Chunk load error detected in promise rejection. Consider reloading the page."
        );
      }
    };

    const handleError = (event: ErrorEvent) => {
      console.error("Global error:", event.error);

      // Check if it's a chunk load error
      if (
        event.error?.message?.includes("Failed to fetch dynamically imported module") ||
        event.error?.message?.includes("Importing a module script failed") ||
        event.error?.message?.toLowerCase().includes("chunk")
      ) {
        console.warn(
          "Chunk load error detected. Consider reloading the page."
        );
      }
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    window.addEventListener("error", handleError);

    return () => {
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
      window.removeEventListener("error", handleError);
    };
  }, []);

  return (
    <AppErrorBoundary>
      <ThemeProvider>
        <AuthProvider {...oidcConfig}>
          <QueryClientProvider client={queryClient}>
            <ProjectProvider>
              <Neo4jVisualizerProvider>
                <PanelPropsProvider>
                  <PanelProvider>
                    <UnsavedChangesProvider>
                      <ChatInputProvider>
                        <AppContent />
                      </ChatInputProvider>
                    </UnsavedChangesProvider>
                  </PanelProvider>
                </PanelPropsProvider>
              </Neo4jVisualizerProvider>
            </ProjectProvider>
          </QueryClientProvider>
        </AuthProvider>
      </ThemeProvider>
    </AppErrorBoundary>
  );
}

export default App;
